import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from './Layout';
import api from '../services/api';
import { Service } from '../types/Service';
import { Lead } from '../types/Lead';
import {
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [service, setService] = useState<Service | null>(null);
  const [assignedLeads, setAssignedLeads] = useState<Lead[]>([]);
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Filter options
  const [stages, setStages] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      loadServiceData();
      loadFilterOptions();
    }
  }, [id]);

  const loadServiceData = async () => {
    setLoading(true);
    try {
      const [serviceRes, leadsRes] = await Promise.all([
        api.get(`/services/${id}`),
        api.get(`/services/${id}/leads`)
      ]);

      setService(serviceRes.data.data);
      setAssignedLeads(leadsRes.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [stagesRes, sourcesRes, segmentsRes, usersRes] = await Promise.all([
        api.get('/leads/stages'),
        api.get('/leads/sources'),
        api.get('/customer-segments'),
        api.get('/invitations/members')
      ]);

      setStages(stagesRes.data?.data || []);
      setSources(sourcesRes.data?.data || []);
      setSegments(segmentsRes.data?.data || []);
      setUsers(usersRes.data?.members?.users || []);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadAvailableLeads = async () => {
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (stageFilter) params.append('stageId', stageFilter);
      if (sourceFilter) params.append('sourceId', sourceFilter);
      if (segmentFilter) params.append('segmentIds', segmentFilter);
      if (assignedFilter) params.append('assignedTo', assignedFilter);

      const response = await api.get(`/leads?${params.toString()}`);
      const leads = response.data.data || [];
      
      // Filter out already assigned leads
      const assignedLeadIds = assignedLeads.map(l => l.id);
      const availableLeads = leads.filter((l: Lead) => !assignedLeadIds.includes(l.id));
      
      setAllLeads(availableLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  useEffect(() => {
    if (showAddLeadsModal) {
      loadAvailableLeads();
    }
  }, [showAddLeadsModal, searchTerm, stageFilter, sourceFilter, segmentFilter, assignedFilter]);

  const handleAssignLeads = async () => {
    if (selectedLeadIds.length === 0) {
      setError('Please select at least one lead');
      return;
    }

    try {
      await api.post(`/services/${id}/assign-leads`, { leadIds: selectedLeadIds });
      setSuccessMessage(`${selectedLeadIds.length} lead(s) assigned successfully`);
      setSelectedLeadIds([]);
      setShowAddLeadsModal(false);
      await loadServiceData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign leads');
    }
  };

  const handleRemoveLead = async (leadId: number) => {
    if (!window.confirm('Remove this lead from the service?')) return;

    try {
      await api.delete(`/services/${id}/leads/${leadId}`);
      setSuccessMessage('Lead removed successfully');
      await loadServiceData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove lead');
    }
  };

  const toggleLeadSelection = (leadId: number) => {
    setSelectedLeadIds(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  if (loading) {
    return (
      <Layout title="Service Details" subtitle="Loading...">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading service...</p>
        </div>
      </Layout>
    );
  }

  if (!service) {
    return (
      <Layout title="Service Not Found" subtitle="Error">
        <div className="text-center py-12">
          <p className="text-gray-600">Service not found</p>
          <button
            onClick={() => navigate('/services')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Services
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={service.name} subtitle="Service Details">
      <div className="space-y-6">
        {/* Header */}
        <button
          onClick={() => navigate('/services')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Services
        </button>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Service Info Card */}
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{service.name}</h2>
              <p className="text-sm text-gray-500 mt-1">Code: <span className="font-mono font-medium">{service.code}</span></p>
              {service.description && (
                <p className="text-gray-600 mt-2">{service.description}</p>
              )}
              
              {/* Segment Assignment Info */}
              <div className="mt-4 flex items-center space-x-2">
                {service.isGlobal ? (
                  <span className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                    Global Service
                  </span>
                ) : (
                  <>
                    <span className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full font-medium">
                      Segment-Specific
                    </span>
                    {service.segmentNames && (
                      <span className="text-sm text-gray-600">
                        Available for: <span className="font-medium">{service.segmentNames}</span>
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <span className={`px-3 py-1 text-xs rounded-full ${service.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {service.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Assigned Leads Section */}
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Assigned Leads</h3>
              <p className="text-sm text-gray-500">{assignedLeads.length} lead(s) assigned to this service</p>
            </div>
            <button
              onClick={() => setShowAddLeadsModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Leads</span>
            </button>
          </div>

          {/* Assigned Leads Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No leads assigned yet. Click "Add Leads" to get started.
                    </td>
                  </tr>
                ) : (
                  assignedLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-blue-600">{lead.leadUid || `#${lead.id}`}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.fullName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {lead.stage_name ? (
                          <span 
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                            style={{ backgroundColor: lead.stage_color }}
                          >
                            {lead.stage_name}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date((lead as any).assigned_at || lead.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleRemoveLead(lead.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove from service"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Leads Modal */}
        {showAddLeadsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Add Leads to Service</h2>
                <button onClick={() => setShowAddLeadsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {/* Filters */}
                <div className="mb-4 space-y-3">
                  {/* Search */}
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Filter Row */}
                  <div className="grid grid-cols-4 gap-3">
                    {segments.length > 0 && (
                      <select
                        value={segmentFilter}
                        onChange={(e) => setSegmentFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">All Segments</option>
                        {segments.map((seg: any) => (
                          <option key={seg.id} value={seg.id}>{seg.name}</option>
                        ))}
                      </select>
                    )}
                    <select
                      value={stageFilter}
                      onChange={(e) => setStageFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">All Stages</option>
                      {stages.map((stage: any) => (
                        <option key={stage.id} value={stage.id}>{stage.name}</option>
                      ))}
                    </select>
                    <select
                      value={sourceFilter}
                      onChange={(e) => setSourceFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">All Sources</option>
                      {sources.map((source: any) => (
                        <option key={source.id} value={source.id}>{source.name}</option>
                      ))}
                    </select>
                    <select
                      value={assignedFilter}
                      onChange={(e) => setAssignedFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">All Assigned</option>
                      <option value="unassigned">Unassigned</option>
                      {users.map((user: any) => (
                        <option key={user.id} value={user.id}>{user.fullName}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Leads Selection List */}
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  {allLeads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No available leads found. Try adjusting your filters.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {allLeads.map((lead) => (
                        <label
                          key={lead.id}
                          className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.includes(lead.id)}
                            onChange={() => toggleLeadSelection(lead.id)}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{lead.fullName}</p>
                                <p className="text-xs text-gray-500">{lead.email}</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                {lead.leadUid && (
                                  <span className="text-xs font-mono text-gray-500">{lead.leadUid}</span>
                                )}
                                {lead.stage_name && (
                                  <span 
                                    className="px-2 py-0.5 text-xs rounded-full text-white"
                                    style={{ backgroundColor: lead.stage_color }}
                                  >
                                    {lead.stage_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {selectedLeadIds.length > 0 && (
                  <div className="mt-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      {selectedLeadIds.length} lead(s) selected
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAddLeadsModal(false);
                    setSelectedLeadIds([]);
                    setSearchTerm('');
                    setStageFilter('');
                    setSourceFilter('');
                    setSegmentFilter('');
                    setAssignedFilter('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignLeads}
                  disabled={selectedLeadIds.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Assign {selectedLeadIds.length > 0 && `(${selectedLeadIds.length})`} Lead(s)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ServiceDetail;
